import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { identifierTypeTranslationMap } from 'src/features/eventLog/utils/constants';
import { GatewayState, MonitoringGateway as MonitoringGatewayType } from 'src/features/monitoring/types';
import { parkingStatusMessages } from 'src/features/monitoring/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { eventTypeTranslationMap } from 'src/features/systems/utils/constants';
import { useGetVisitor } from 'src/features/visitor/api/useGetVisitor';
import { GatewayAction, GatewayActionState } from 'src/features/visitor/types';
import { visitorTypeBadgeOptions } from 'src/features/visitor/utils/constants';
import {
    GeneralBlock,
    GeneralBlockHead,
    GeneralBlockHeader,
    GeneralBlockSubtitle,
    GeneralBlockTitle,
} from 'src/registry/components/blocks/GeneralBlock';
import StatisticValue from 'src/registry/components/statistic/StatisticValue';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';
import { Translation } from 'src/types';
import { PERMISSION } from 'src/features/auth';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import WebRTCVideoPlayer from 'src/features/videoPlayer/WebRTCVideoPlayer';
import { VideoSkeleton } from 'src/registry/ui/Skeleton';
import { cameraMessages } from 'src/features/camera/utils/messages';
import CameraModal from 'src/features/camera/components/CameraModal';

export const translate = (translations: Translation[], locale: string) =>
    translations.find((translation) => translation.language === locale);

type Props = {
    gateway: MonitoringGatewayType;
    isOpen: boolean;
    visitors?: {
        currentLicensePlate?: string;
        pastVisitorId?: string;
    };
    isBusy?: boolean;
    /** Lifted out of `gateway` so the parent can patch it from the
     *  `gateway_state_changed` WS event without re-fetching the list. */
    currentState?: GatewayState | null;
    setGatewayAction: (v: GatewayActionState) => void;
};
export default function SingleMonitoringGateway({
    gateway,
    isOpen,
    isBusy,
    visitors,
    currentState,
    setGatewayAction,
}: Props) {
    const ref = React.useRef<HTMLDivElement>(null);
    const { parkingId } = useParking();

    const accessGatewaysActions = useHasPermissions([PERMISSION.ACCESS_GATEWAYS_MONITORING_ACTIONS]);
    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);

    const gatewayHasCamera = gateway.has_camera;

    const [selectedGatewayCamera, setSelectedGatewayCamera] = React.useState<string>('');
    const [clickedPlay, setClickedPlay] = React.useState<boolean>(false);
    const [isPlaying, setIsPlaying] = React.useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<boolean>(false);

    const { formatMessage, locale } = useIntl();
    const { data } = useGetVisitor({
        parkingId,
        visitorId: visitors?.pastVisitorId || '',
        config: { enabled: !!visitors?.pastVisitorId },
    });

    const { translation, entryZone, exitZone } = React.useMemo(() => {
        const translation = translate(gateway.translations, locale);
        const entryZone = gateway.entry_zone ? translate(gateway.entry_zone.translations, locale)?.name : '-';
        const exitZone = gateway.exit_zone ? translate(gateway.exit_zone.translations, locale)?.name : '-';

        return {
            translation,
            entryZone,
            exitZone,
        };
    }, [gateway, locale]);

    React.useEffect(() => {
        if (!ref.current) return;

        if (isOpen) ref.current.style.height = String(ref.current.scrollHeight) + 'px';
        else ref.current.style.height = '0px';
    }, [isOpen]);

    const hiddenCameraStream = selectedGatewayCamera === gateway.id && isPlaying;
    const showPlayButton = !isPlaying && loading && !clickedPlay && !error;
    const showLoadingState = clickedPlay && loading && !isPlaying;

    const handleClicked = () => {
        setSelectedGatewayCamera(gateway.id);
        setClickedPlay(!clickedPlay);
    };

    return (
        <div className="flex flex-col">
            <GeneralBlock className="flex-1 rounded-b-none border-b-0">
                <div className="flex w-full flex-row items-center justify-between">
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle className="text-base text-gray-900">
                                {translation?.name}
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle className="text-sm">
                                {translation?.description || '-'}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <Badge theme={isBusy ? 'red' : 'green'}>
                        {formatMessage(isBusy ? parkingStatusMessages.busy : parkingStatusMessages.free)}
                    </Badge>
                </div>
                <div className="relative aspect-video w-full">
                    {clickedPlay && !isModalOpen && (
                        <WebRTCVideoPlayer
                            selectedGateway={selectedGatewayCamera}
                            setIsPlaying={setIsPlaying}
                            loading={loading}
                            setLoading={setLoading}
                            setError={setError}
                        />
                    )}

                    <button
                        className={cn(
                            'group xs:aspect-auto bg-gray-300" absolute top-0 left-0 flex aspect-video h-full w-full items-center justify-center overflow-hidden rounded-xl bg-gray-200',
                            hiddenCameraStream && 'bg-transparent',
                            gatewayHasCamera && 'cursor-pointer',
                        )}
                        disabled={!gatewayHasCamera}
                        onClick={handleClicked}
                    >
                        {showPlayButton && (
                            <FontAwesomeIcon
                                icon={ICON_MAP.PLAY}
                                aria-hidden="true"
                                focusable="false"
                                className={cn(
                                    'absolute scale-200 text-white',
                                    hiddenCameraStream && 'text-transparent group-hover:text-white',
                                    gatewayHasCamera && 'transition-transform group-hover:scale-250',
                                )}
                            />
                        )}
                        {showLoadingState && <VideoSkeleton className="absolute top-0 left-0 size-full" />}

                        {error && (
                            <div className="flex justify-center">
                                <FontAwesomeIcon
                                    icon={ICON_MAP.RETRY}
                                    aria-hidden="true"
                                    focusable="false"
                                    className={cn('absolute scale-140 text-white')}
                                    onClick={handleClicked}
                                />
                                <div className="mt-6 text-xs text-red-700">
                                    {formatMessage(cameraMessages.streamUnavailableError)}
                                </div>
                            </div>
                        )}
                    </button>
                </div>
                <div className="relative">
                    {clickedPlay && (
                        <CameraModal
                            gateway={gateway}
                            isModalOpen={isModalOpen}
                            setIsModalOpen={setIsModalOpen}
                            isPlaying={isPlaying}
                        >
                            <WebRTCVideoPlayer
                                selectedGateway={gateway.id}
                                setIsPlaying={setIsPlaying}
                                loading={loading}
                                setLoading={setLoading}
                                setError={setError}
                            />
                        </CameraModal>
                    )}
                </div>

                <div
                    className={cn('flex flex-col gap-4 overflow-hidden duration-500 ease-in-out', !isOpen && '-mt-4')}
                    ref={ref}
                >
                    <div className="flex w-full justify-center gap-5 text-center text-xs">
                        <StatisticValue
                            title={formatMessage(parkingStatusMessages.totalPassesToday)}
                            value={gateway.total_passes_today}
                        />
                        <StatisticValue
                            title={formatMessage(parkingStatusMessages.failedPassesToday)}
                            value={gateway.failed_passes_today}
                        />
                    </div>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.entryZone} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle>{entryZone}</GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.exitZone} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle>{exitZone}</GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.currentVisitor} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle className="flex gap-2">
                                {visitors?.currentLicensePlate ?? '-'}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.lastVisitor} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle className="flex gap-2">
                                {data ? (
                                    <>
                                        <LinkWithIcon
                                            to={`/${PATH.VISITORS}/visitor/${data.id}`}
                                            linkTitle={data?.main_identifier?.value ?? ''}
                                            hasPermissions={accessVisitors}
                                        />
                                        <Badge theme={visitorTypeBadgeOptions[data.type].theme}>
                                            <FontAwesomeIcon
                                                icon={visitorTypeBadgeOptions[data.type].icon || ICON_MAP.CLIENT}
                                            />
                                            {formatMessage(visitorTypeBadgeOptions[data.type].label)}
                                        </Badge>
                                    </>
                                ) : (
                                    '-'
                                )}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.requiredIdentifiers} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle>
                                {gateway.required_identifier_types
                                    .map((type) =>
                                        formatMessage(
                                            identifierTypeTranslationMap[type] ?? {
                                                id: 'common.unknown',
                                                defaultMessage: type,
                                            },
                                        ),
                                    )
                                    .join(', ')}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.state} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle className="flex gap-2">
                                {currentState ? (
                                    <Badge theme={currentState === 'opened' ? 'green' : 'red'}>
                                        {formatMessage(
                                            currentState === 'opened'
                                                ? parkingStatusMessages.opened
                                                : parkingStatusMessages.closed,
                                        )}
                                    </Badge>
                                ) : (
                                    '-'
                                )}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.message} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle>
                                {gateway.message
                                    ? formatMessage(
                                          eventTypeTranslationMap[gateway.message] ?? {
                                              id: 'common.unknown',
                                              defaultMessage: gateway.message,
                                          },
                                      )
                                    : '-'}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                </div>
            </GeneralBlock>

            {accessGatewaysActions && (
                <GeneralBlock className="flex w-full shrink flex-row gap-2 rounded-t-none">
                    <Button
                        className="flex-1"
                        onClick={() =>
                            setGatewayAction({
                                gateway: { name: translation?.name || '', id: gateway.id },
                                action: GatewayAction.OPEN,
                            })
                        }
                        size="sm"
                    >
                        <FontAwesomeIcon icon={ICON_MAP.ARROW_UP_RIGHT} />
                        <span className="xs:block hidden lg:block">
                            <FormattedMessage {...parkingStatusMessages.raise} />
                        </span>
                    </Button>
                    <Button
                        className="flex-1"
                        variant="destructiveLight"
                        onClick={() =>
                            setGatewayAction({
                                gateway: { name: translation?.name || '', id: gateway.id },
                                action: GatewayAction.EMERGENCY,
                            })
                        }
                        size="sm"
                    >
                        <FontAwesomeIcon icon={ICON_MAP.DANGER} />
                        <span className="xs:block hidden lg:block">
                            <FormattedMessage {...parkingStatusMessages.emergency} />
                        </span>
                    </Button>
                    <Button
                        className="flex-1"
                        variant="outline"
                        onClick={() =>
                            setGatewayAction({
                                gateway: { name: translation?.name || '', id: gateway.id },
                                action: GatewayAction.LOCK,
                            })
                        }
                        size="sm"
                    >
                        <FontAwesomeIcon icon={ICON_MAP.PASSWORD} />
                        <span className="xs:block hidden lg:block">
                            <FormattedMessage {...parkingStatusMessages.lock} />
                        </span>
                    </Button>
                </GeneralBlock>
            )}
        </div>
    );
}
